
import { useState, useCallback, useRef, useEffect } from 'react';
import { getUserUsageStatsWithDefaults, UserUsageStats } from '@/services/ai-message-limits-service';
import { TopicType } from '@/types/assistant';

interface UseUsageStateManagerProps {
  userId: string | null;
  onError?: (error: Error) => void;
}

interface UsageStateManager {
  usageStats: UserUsageStats[];
  isLoading: boolean;
  refreshUsageStats: () => Promise<void>;
  incrementUsageOptimistically: (topic: TopicType) => void;
  getUsageForTopic: (topic: TopicType) => UserUsageStats | null;
}

export const useUsageStateManager = ({ 
  userId, 
  onError 
}: UseUsageStateManagerProps): UsageStateManager => {
  const [usageStats, setUsageStats] = useState<UserUsageStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch real usage stats from subscription (with ref to prevent re-creation)
  const fetchUsageStatsRef = useRef<() => Promise<void>>();
  
  const fetchUsageStats = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const stats = await getUserUsageStatsWithDefaults(userId);
      setUsageStats(stats);
    } catch (error) {
      console.error('[USAGE STATE] Error fetching usage stats:', error);
      
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
      
      // Use free package defaults on error
      setUsageStats([
        { topic: 'guide', daily_usage: 0, monthly_usage: 0, daily_limit: 5, monthly_limit: 100, package_name: 'free' },
        { topic: 'performance', daily_usage: 0, monthly_usage: 0, daily_limit: 3, monthly_limit: 60, package_name: 'free' },
        { topic: 'trading_strategy', daily_usage: 0, monthly_usage: 0, daily_limit: 2, monthly_limit: 40, package_name: 'free' },
        { topic: 'advisor', daily_usage: 0, monthly_usage: 0, daily_limit: 1, monthly_limit: 20, package_name: 'free' }
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [userId]); // Remove onError dependency to prevent re-fetching loops
  
  fetchUsageStatsRef.current = fetchUsageStats;

  // Initial fetch with dependency control
  useEffect(() => {
    if (userId) {
      fetchUsageStats();
    }
  }, [userId]); // Only depend on userId, not fetchUsageStats

  // Refresh usage stats from database
  const refreshUsageStats = useCallback(async () => {
    if (fetchUsageStatsRef.current) {
      await fetchUsageStatsRef.current();
    }
  }, []); // No dependencies to prevent re-creation

  // Optimistic increment - update usage count locally before server response
  const incrementUsageOptimistically = useCallback((topic: TopicType) => {
    setUsageStats(prevStats => 
      prevStats.map(stat => 
        stat.topic === topic 
          ? { 
              ...stat, 
              daily_usage: stat.daily_usage + 1,
              monthly_usage: stat.monthly_usage + 1
            }
          : stat
      )
    );
  }, []);

  // Get usage for specific topic
  const getUsageForTopic = useCallback((topic: TopicType): UserUsageStats | null => {
    const foundStat = usageStats.find(stat => stat.topic === topic);
    
    if (foundStat) {
      return foundStat;
    }
    
    // Return free package default if not found
    const defaultLimits = {
      guide: { daily: 5, monthly: 100 },
      performance: { daily: 3, monthly: 60 },
      trading_strategy: { daily: 2, monthly: 40 },
      advisor: { daily: 1, monthly: 20 }
    };
    
    const limits = defaultLimits[topic] || { daily: 1, monthly: 20 };
    
    return {
      topic,
      daily_usage: 0,
      monthly_usage: 0,
      daily_limit: limits.daily,
      monthly_limit: limits.monthly,
      package_name: 'free'
    };
  }, [usageStats]);

  return {
    usageStats,
    isLoading,
    refreshUsageStats,
    incrementUsageOptimistically,
    getUsageForTopic
  };
};
